import { useTranslation } from 'react-i18next';

import type { CarrierService } from '@/api/types/services';
import { Switch } from '@/components/ui/switch';
import { resolveLocale, toIntlLocale } from '@/i18n';
import { formatCurrency, formatNumber } from '@/lib/format/currency';

/**
 * Figma summary table for a configured method: gray panel aligned under the row's
 * name column, header row (Cost / Free Shipping / Nearby Points / Max Weight /
 * Status) and one value row - nearby/weight as small chips, the live status
 * toggle right-aligned in the Status column.
 */
export function MethodSummary({
  service,
  requiresPointSelection,
  onToggle,
  togglePending,
}: {
  service: CarrierService;
  requiresPointSelection: boolean;
  onToggle: (is_enabled: boolean) => void;
  togglePending: boolean;
}) {
  const { t, i18n } = useTranslation('markets');
  const locale = toIntlLocale(resolveLocale(i18n.language));

  const weightUnit = service.max_weight_unit ? t(`weightUnits.${service.max_weight_unit}`) : '';

  return (
    <div className="bg-[#f7f7f7] px-4 py-3 md:pl-[117px] md:pr-[37px]">
      <div className="flex items-start">
        {[
          t('summary.cost'),
          t('summary.freeShipping'),
          // Nearby Points only means something where a point picker exists.
          ...(requiresPointSelection ? [t('summary.nearbyPoints')] : []),
          t('summary.maxWeight'),
        ].map((header) => (
          <div
            key={header}
            className="flex h-10 flex-1 items-center border-b border-[#e5e5e5] px-2 text-sm font-medium text-foreground"
          >
            {header}
          </div>
        ))}
        <div className="flex h-10 flex-1 items-center justify-end border-b border-[#e5e5e5] px-2 text-sm font-medium text-foreground">
          {t('summary.status')}
        </div>
      </div>
      <div className="flex items-center">
        <div className="flex h-14 flex-1 items-center p-2 text-base text-foreground">
          {formatCurrency(service.flat_rate_price, service.currency, locale)}
        </div>
        <div className="flex h-14 flex-1 items-center p-2 text-base text-foreground">
          {service.free_shipping_threshold != null
            ? formatCurrency(service.free_shipping_threshold, service.currency, locale)
            : t('summary.none')}
        </div>
        {requiresPointSelection ? (
          <div className="flex h-14 flex-1 items-center gap-1.5 p-2">
            {service.nearby_points_count != null ? (
              <>
                <span className="flex h-6 items-center justify-center rounded-md bg-[#e1e1e1] px-2 text-base text-foreground">
                  {service.nearby_points_count}
                </span>
                {service.point_type ? (
                  <span className="flex h-6 items-center justify-center rounded-md bg-[#e1e1e1] px-2 text-xs text-muted-foreground">
                    {t(`summary.pointType.${service.point_type}`)}
                  </span>
                ) : null}
              </>
            ) : (
              <span className="text-base text-muted-foreground">{t('summary.none')}</span>
            )}
          </div>
        ) : null}
        <div className="flex h-14 flex-1 items-center p-2">
          {service.max_weight != null ? (
            <span className="flex h-6 items-center justify-center rounded-md bg-accent px-2 text-base text-foreground">
              {`${formatNumber(service.max_weight, locale)} ${weightUnit}`.trim()}
            </span>
          ) : (
            <span className="text-base text-muted-foreground">{t('summary.none')}</span>
          )}
        </div>
        <div className="flex h-14 flex-1 items-center justify-end p-2">
          <Switch
            checked={service.is_enabled}
            onCheckedChange={onToggle}
            disabled={togglePending}
            aria-label={t('summary.status')}
          />
        </div>
      </div>
    </div>
  );
}
